import { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  QUOTATION_STATUS_MAP,
  type QuotationStatus,
  type TenantSettings,
} from "@goyone/shared";
import { api, qs } from "../../lib/api";
import { issueInvoiceFromQuotation } from "../../lib/invoiceIssue";
import { useTenantProfile } from "../../lib/settings";
import { date, money } from "../../lib/format";
import { Icon } from "../../components/Icon";
import { DocPreview } from "../../components/DocPreview";
import { Badge, ErrorState, Modal, PageHead } from "../../components/ui";
import { DeleteButton } from "../../components/DeleteDialog";
import { Attachments } from "../../components/Attachments";
import {
  emptyLine,
  LineEditor,
  toPayload,
  type EditableLine,
} from "./LineEditor";
import { QualifyPrompt } from "./QuotationActions";

interface Quotation {
  id: string;
  number: string;
  status: QuotationStatus;
  title: string | null;
  notes: string | null;
  terms: string | null;
  issuedAt: string;
  validUntil: string | null;
  subtotal: string;
  discount: string;
  taxRate: string;
  taxAmount: string;
  total: string;
  rejectReason: string | null;
  account: { id: string; name: string } | null;
  /** العرضُ على محتملٍ لم يُحوَّل بعد. */
  lead?: { id: string; name: string; status: string } | null;
  lines: Array<{
    serviceId: string | null;
    description: string;
    quantity: string;
    unit: string;
    unitPrice: string;
    discount: string;
    lineTotal: string;
  }>;
  invoices: Array<{
    id: string;
    number: string;
    status: string;
    total: string;
  }>;
  /* عقد العرض إن قام — به تُرتَّب أزرار السلسلة: قبولٌ فعقدٌ ففاتورة. */
  contracts: Array<{
    id: string;
    number: string;
    status: string;
    signedAt: string | null;
  }>;
}

interface AccountOption {
  id: string;
  name: string;
}

export function QuotationDetail() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  const quotation = useQuery({
    queryKey: ["quotation", id],
    queryFn: () => api<Quotation>(`/sales/quotations/${id}`),
    enabled: !isNew,
  });

  if (isNew) {
    return (
      <QuotationForm
        presetAccountId={params.get("accountId")}
        presetLeadId={params.get("leadId")}
        fromAccount={params.get("from") === "account"}
        fromLead={params.get("from") === "lead"}
      />
    );
  }

  if (quotation.isError) {
    return (
      <div className="page">
        <ErrorState error={quotation.error} />
      </div>
    );
  }

  if (quotation.isPending) {
    return (
      <div className="page">
        <div
          className="skeleton"
          style={{ height: 28, width: 220, marginBottom: 20 }}
        />
        <div className="skeleton" style={{ height: 260 }} />
      </div>
    );
  }

  const doc = quotation.data;
  const statusDef = QUOTATION_STATUS_MAP[doc.status];

  return (
    <QuotationView
      doc={doc}
      onChanged={() => {
        void queryClient.invalidateQueries({ queryKey: ["quotation", id] });
        void queryClient.invalidateQueries({ queryKey: ["quotations"] });
      }}
      onInvoiced={(invoiceId) => {
        void queryClient.invalidateQueries();
        navigate(`/finance/invoices/${invoiceId}`);
      }}
      statusLabel={statusDef?.label ?? doc.status}
      statusKind={statusDef?.kind ?? "neutral"}
    />
  );
}

/* ==========================================================================
   عرض المستند مع إجراءاته
   ========================================================================== */

function QuotationView({
  doc,
  onChanged,
  onInvoiced,
  statusLabel,
  statusKind,
}: {
  doc: Quotation;
  onChanged: () => void;
  onInvoiced: (invoiceId: string) => void;
  statusLabel: string;
  statusKind: string;
}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [previewing, setPreviewing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [qualify, setQualify] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: (op: { path: string; body?: unknown }) =>
      api(`/sales/quotations/${doc.id}${op.path}`, {
        method: "POST",
        body: op.body ?? {},
      }),
    onSuccess: (r, op) => {
      setRejecting(false);
      onChanged();
      /* قبولُ عرضِ محتملٍ يفتح دعوةَ التحويل في مكانه. */
      const res = r as
        { needsConversion?: boolean; leadId?: string | null } | undefined;
      if (op.path === "/decide" && res?.needsConversion && res.leadId)
        setQualify(res.leadId);
    },
  });

  const invoice = useMutation({
    mutationFn: () => issueInvoiceFromQuotation(doc.id),
    onSuccess: (created) => onInvoiced(created.id),
  });

  /* العقد من العرض — الخطوة بين القبول والفاتورة، في مكانها من السلسلة. */
  const contractM = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/sales/contracts/from-quotation", {
        method: "POST",
        body: {
          quotationId: doc.id,
          includeLines: true,
          includeDefaults: true,
        },
      }),
    onSuccess: () => onChanged(),
  });

  const lines: EditableLine[] = doc.lines.map((l) => ({
    serviceId: l.serviceId,
    description: l.description,
    quantity: String(Number(l.quantity)),
    unit: l.unit,
    unitPrice: String(Number(l.unitPrice)),
    discount: String(Number(l.discount)),
  }));

  const error = act.error ?? invoice.error ?? contractM.error;
  const alreadyInvoiced = doc.invoices.some((i) => i.status !== "CANCELLED");
  /*
   * السلسلة بعد القبول: عقدٌ يُنشأ، فيُوقَّع ويُفعَّل، فتصدر الفاتورة.
   * كل زرٍّ يظهر في أوانه وحده — والخادم يحرس الترتيب نفسه من كل باب.
   */
  const contract = doc.contracts?.[0] ?? null;
  const contractReady = Boolean(
    contract && contract.signedAt && contract.status === "ACTIVE",
  );
  const fromAccountParam =
    params.get("from") === "account" ? "?from=account" : "";

  return (
    <div className="page">
      <PageHead
        title={`عرض سعر ${doc.number}`}
        subtitle={doc.title ?? undefined}
        actions={
          <>
            {/* من جاء من ملف العميل يعود إليه — لا إلى قائمةٍ لم يمرّ بها. */}
            {params.get("from") === "account" && doc.account?.id && (
              <Link to={`/crm/accounts/${doc.account.id}`} className="btn">
                <Icon name="arrowLeft" size={15} />
                ملف العميل
              </Link>
            )}
            <Link to="/sales/quotations" className="btn">
              <Icon name="arrowLeft" size={15} />
              رجوع
            </Link>

            {/* معاينةٌ داخل النظام ثم طباعةٌ منها — بلا مغادرة الشاشة. */}
            <button className="btn" onClick={() => setPreviewing(true)}>
              <Icon name="search" size={15} />
              معاينة وطباعة
            </button>

            {doc.status === "DRAFT" && (
              <button
                className="btn btn-primary"
                onClick={() => act.mutate({ path: "/send" })}
                disabled={act.isPending}
              >
                تعليم كمُرسَل
              </button>
            )}

            {/*
              القرارُ يُتاح للمُرسَل ولما يُتابَع سواءً — ولو قُصر على
              «أُرسل» لبقي عرضٌ نُقل إلى المتابعة بلا أزرارٍ يُحسم بها.
              وللمُرسَل وحده زرُّ نقلٍ إلى المتابعة: لا يُرجَع منها.
            */}
            {(doc.status === "SENT" || doc.status === "NEGOTIATION") && (
              <>
                {doc.status === "SENT" && (
                  <button
                    className="btn"
                    onClick={() => act.mutate({ path: "/follow" })}
                    disabled={act.isPending}
                    title="انقله إلى «قيد المتابعة» — بين الإرسال والقرار"
                  >
                    <Icon name="clock" size={15} />
                    بدء المتابعة
                  </button>
                )}
                <button
                  className="btn"
                  onClick={() => setRejecting(true)}
                  disabled={act.isPending}
                >
                  تسجيل رفض
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    act.mutate({
                      path: "/decide",
                      body: { decision: "ACCEPTED" },
                    })
                  }
                  disabled={act.isPending}
                >
                  <Icon name="check" size={15} />
                  تسجيل قبول
                </button>
              </>
            )}

            {/* مقبولٌ بلا عقد: الخطوة التالية عقدٌ لا فاتورة. */}
            {doc.status === "ACCEPTED" && !contract && (
              <button
                className="btn btn-primary"
                onClick={() => contractM.mutate()}
                disabled={contractM.isPending}
              >
                <Icon name="clipboard" size={15} />
                {contractM.isPending ? "جارٍ الإنشاء" : "إنشاء عقد للعرض"}
              </button>
            )}

            {/* عقد العرض حاضرٌ بزرّه — ومنه يُوقَّع ويُفعَّل. */}
            {contract && (
              <Link
                to={`/sales/contracts/${contract.id}${fromAccountParam}`}
                className="btn"
                title={
                  contractReady
                    ? "العقد موقَّع وسارٍ"
                    : "افتح العقد لتوقيعه وتفعيله — الفاتورة تصدر بعدهما"
                }
              >
                <Icon name="clipboard" size={15} />
                عقد {contract.number}
              </Link>
            )}

            {/* الفاتورة لا تظهر إلا عن عقدٍ موقَّعٍ سارٍ — كما يحكم الخادم. */}
            {doc.status === "ACCEPTED" && contractReady && !alreadyInvoiced && (
              <button
                className="btn btn-primary"
                onClick={() => invoice.mutate()}
                disabled={invoice.isPending}
              >
                {invoice.isPending ? "جارٍ الإصدار" : "إصدار فاتورة"}
              </button>
            )}

            {/*
              الزر معروض في كل الحالات، والمنع يأتي من الفحص لا من إخفائه.
              عرضٌ صادر عنه فاتورة أو عقد يردّه الخادم بسبب مذكور وبديل
              مقترح، وهو أوضح لمن يبحث عن الزر من غيابه بلا تفسير.
            */}
            <DeleteButton
              entity="quotation"
              id={doc.id}
              endpoint={`/sales/quotations/${doc.id}`}
              onDeleted={() => navigate("/sales/quotations")}
            />
          </>
        }
      />

      {error && (
        <div className="alert alert-danger mb-4">
          {error instanceof Error ? error.message : "تعذّر إتمام العملية."}
        </div>
      )}

      {doc.status === "DRAFT" && (
        <DiscountBalanceHint
          quoteDiscount={
            Number(doc.discount) +
            doc.lines.reduce((n, l) => n + Number(l.discount), 0)
          }
        />
      )}

      {doc.status === "REJECTED" && doc.rejectReason && (
        <div className="alert alert-warning mb-4">
          <Icon name="alert" size={18} />
          <div>سبب الرفض: {doc.rejectReason}</div>
        </div>
      )}

      {doc.invoices.length > 0 && (
        <div className="alert alert-info mb-4">
          <Icon name="wallet" size={18} />
          <div>
            صدرت عن هذا العرض:{" "}
            {doc.invoices.map((i, idx) => (
              <span key={i.id}>
                {idx > 0 && "، "}
                <Link to={`/finance/invoices/${i.id}`}>{i.number}</Link>
              </span>
            ))}
          </div>
        </div>
      )}

      {qualify && (
        <QualifyPrompt
          leadId={qualify}
          quotationId={doc.id}
          number={doc.number}
          onClose={() => setQualify(null)}
        />
      )}
      <div className="doc-head">
        <div>
          <div className="doc-number">{doc.number}</div>
          <div className="small mute">
            {doc.account ? (
              <Link to={`/crm/accounts/${doc.account.id}`}>
                {doc.account.name}
              </Link>
            ) : doc.lead ? (
              <Link to={`/crm/leads/${doc.lead.id}`}>
                {doc.lead.name} — عميل محتمل
              </Link>
            ) : (
              "—"
            )}
          </div>
        </div>
        <div className="col" style={{ gap: 2 }}>
          <span className="tiny mute">تاريخ الإصدار</span>
          <span>{date(doc.issuedAt)}</span>
        </div>
        <div className="col" style={{ gap: 2 }}>
          <span className="tiny mute">صالح حتى</span>
          <span>{date(doc.validUntil)}</span>
        </div>
        <div style={{ marginInlineStart: "auto" }}>
          <Badge kind={statusKind}>{statusLabel}</Badge>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <LineEditor
            lines={lines}
            onChange={() => undefined}
            discount={String(Number(doc.discount))}
            onDiscountChange={() => undefined}
            taxRate={String(Number(doc.taxRate))}
            onTaxRateChange={() => undefined}
            disabled
          />

          {(doc.notes || doc.terms) && (
            <div className="mt-4">
              {doc.notes && (
                <>
                  <div className="label">ملاحظات</div>
                  <p className="small mb-4" style={{ whiteSpace: "pre-wrap" }}>
                    {doc.notes}
                  </p>
                </>
              )}
              {doc.terms && (
                <>
                  <div className="label">الشروط</div>
                  <p className="small" style={{ whiteSpace: "pre-wrap" }}>
                    {doc.terms}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {rejecting && (
        <Modal
          title="تسجيل رفض العرض"
          onClose={() => setRejecting(false)}
          footer={
            <>
              <button
                className="btn"
                onClick={() => setRejecting(false)}
                type="button"
              >
                إلغاء
              </button>
              <button
                className="btn btn-danger"
                onClick={() =>
                  act.mutate({
                    path: "/decide",
                    body: { decision: "REJECTED", rejectReason },
                  })
                }
                disabled={rejectReason.trim().length < 2 || act.isPending}
              >
                تسجيل الرفض
              </button>
            </>
          }
        >
          <div className="field">
            <label className="label req" htmlFor="reject-reason">
              سبب الرفض
            </label>
            <textarea
              id="reject-reason"
              className="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="السعر مرتفع، اختار منافسًا، تأجّل القرار..."
              autoFocus
            />
            <span className="tiny mute">
              السبب يظهر في تقارير أسباب الخسارة، فيوضّح أين تُفقد الصفقات.
            </span>
          </div>
        </Modal>
      )}
      <div className="mt-4">
        <Attachments
          entity="quotation"
          entityId={doc.id}
          title="مرفقات العرض"
        />
      </div>

      {previewing && (
        <DocPreview
          path={`/print/quotation/${doc.id}`}
          title={`عرض سعر ${doc.number ?? ""}`}
          onClose={() => setPreviewing(false)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   إنشاء عرض جديد
   ========================================================================== */

/**
 * الانتظار حتى تصل الإعدادات قبل بناء النموذج.
 *
 * البديل بناؤه بقيم معلَّبة ثم استبدالها عند وصول الإعدادات، فتتغير نسبة
 * الضريبة والشروط تحت يد المستخدم وهو يكتب. الانتظار جزء من الثانية أهون.
 */
function QuotationForm({
  presetAccountId,
  presetLeadId = null,
  fromAccount = false,
  fromLead = false,
}: {
  presetAccountId: string | null;
  presetLeadId?: string | null;
  fromAccount?: boolean;
  fromLead?: boolean;
}) {
  const profile = useTenantProfile();

  if (!profile.data) {
    return (
      <div className="page">
        <div className="skeleton" style={{ height: 380 }} />
      </div>
    );
  }

  return (
    <QuotationFormFields
      presetAccountId={presetAccountId}
      presetLeadId={presetLeadId}
      fromAccount={fromAccount}
      fromLead={fromLead}
      defaults={profile.data.settings}
    />
  );
}

function QuotationFormFields({
  presetAccountId,
  presetLeadId = null,
  fromAccount = false,
  fromLead = false,
  defaults,
}: {
  presetAccountId: string | null;
  presetLeadId?: string | null;
  fromAccount?: boolean;
  fromLead?: boolean;
  defaults: TenantSettings;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [accountId, setAccountId] = useState(presetAccountId ?? "");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState(defaults.defaultQuotationTerms);
  const [discount, setDiscount] = useState("0");
  const [taxRate, setTaxRate] = useState(String(defaults.taxRate));
  const [lines, setLines] = useState<EditableLine[]>([emptyLine()]);

  /* العرضُ على محتمل: يُقرأ اسمُه ويُثبَّت — لا حسابَ يُختار. */
  const lead = useQuery({
    queryKey: ["lead", presetLeadId],
    enabled: Boolean(presetLeadId),
    queryFn: () =>
      api<{
        id: string;
        name: string;
        status: string;
        convertedAccountId: string | null;
      }>(`/crm/leads/${presetLeadId}`),
  });

  const accounts = useQuery({
    queryKey: ["accounts", "options"],
    queryFn: () =>
      api<{ items: AccountOption[] }>(
        `/crm/accounts${qs({ pageSize: 100, sort: "name", order: "asc" })}`,
      ),
  });

  /* الحساب الأول يُختار تلقائيًا إن لم يُمرَّر، لتقليل خطوة في الحالة الشائعة. */
  useEffect(() => {
    if (presetLeadId) return;
    if (!accountId && accounts.data?.items.length === 1) {
      setAccountId(accounts.data.items[0]!.id);
    }
  }, [accounts.data, accountId, presetLeadId]);

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/sales/quotations", {
        method: "POST",
        body: {
          accountId: presetLeadId ? undefined : accountId,
          leadId: presetLeadId ?? undefined,
          title: title || undefined,
          notes: notes || undefined,
          terms: terms || undefined,
          discount: Number(discount) || 0,
          taxRate: Number(taxRate) || 0,
          lines: toPayload(lines),
        },
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["quotations"] });
      navigate(`/sales/quotations/${created.id}`);
    },
  });

  const validLines = toPayload(lines);
  const canSave =
    (!!accountId || !!presetLeadId) &&
    validLines.length > 0 &&
    !create.isPending;

  return (
    <div className="page">
      <PageHead
        title="عرض سعر جديد"
        subtitle="يُحفظ كمسودة، ويُعطى رقمه عند الحفظ"
        actions={
          <>
            {/* من جاء من ملف العميل يعود إليه بعلامةٍ صريحة. */}
            {fromAccount && presetAccountId && (
              <Link to={`/crm/accounts/${presetAccountId}`} className="btn">
                <Icon name="arrowLeft" size={15} />
                ملف العميل
              </Link>
            )}
            {fromLead && presetLeadId && (
              <Link to={`/crm/leads/${presetLeadId}`} className="btn">
                <Icon name="arrowLeft" size={15} />
                ملف العميل المحتمل
              </Link>
            )}
            <Link to="/sales/quotations" className="btn">
              <Icon name="arrowLeft" size={15} />
              رجوع
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => create.mutate()}
              disabled={!canSave}
            >
              {create.isPending ? "جارٍ الحفظ" : "حفظ"}
            </button>
          </>
        }
      />

      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error
            ? create.error.message
            : "تعذّر الحفظ."}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <div className="form-grid">
            {presetLeadId ? (
              <div className="field">
                <span className="label">العميل المحتمل</span>
                <div
                  className="input"
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <Icon name="user" size={14} />
                  {lead.data?.name ?? "…"}
                  <span className="tiny mute">
                    — العرضُ على محتمل، ويتبعه إلى حسابه حين يُحوَّل
                  </span>
                </div>
              </div>
            ) : (
              <div className="field">
                <label className="label req" htmlFor="q-account">
                  الحساب
                </label>
                <select
                  id="q-account"
                  className="select"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                >
                  <option value="">اختر حسابًا</option>
                  {accounts.data?.items.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="field span-2">
              <label className="label" htmlFor="q-title">
                عنوان العرض
              </label>
              <input
                id="q-title"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="تطوير الهوية البصرية"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-head">
          <h2 className="card-title">البنود</h2>
        </div>
        <div className="card-body">
          <LineEditor
            lines={lines}
            onChange={setLines}
            discount={discount}
            onDiscountChange={setDiscount}
            taxRate={taxRate}
            onTaxRateChange={setTaxRate}
          />
        </div>
      </div>

      <DiscountBalanceHint
        quoteDiscount={
          (Number(discount) || 0) +
          toPayload(lines).reduce((n, l) => n + (l.discount || 0), 0)
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="form-grid">
            <div className="field">
              <label className="label" htmlFor="q-notes">
                ملاحظات
              </label>
              <textarea
                id="q-notes"
                className="textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="q-terms">
                الشروط
              </label>
              <textarea
                id="q-terms"
                className="textarea"
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                placeholder="مدة التنفيذ، شروط السداد، ما لا يشمله العرض"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MineBalance {
  enforcement: "OFF" | "WARN" | "BLOCK";
  employee: {
    discountBudget: number | null;
    discountUsed: number;
    discountRemaining: number | null;
  } | null;
}

/**
 * تنبيه رصيد الخصومات — يُظهر للمندوب متبقّيه قبل الإرسال.
 *
 * يعرض رصيد المستخدم الحالي: هو المندوب في الغالب، والخادم يُلزم على مالك
 * العرض الفعليّ عند الإرسال. التنبيه هنا إرشادٌ سابق لا الحدّ نفسه، فلا يظهر
 * حين تكون السياسة معطّلة أو لا رصيد مضبوطًا.
 */
function DiscountBalanceHint({ quoteDiscount }: { quoteDiscount: number }) {
  const mine = useQuery({
    queryKey: ["targets", "mine"],
    queryFn: () => api<MineBalance>("/sales/targets/mine"),
  });

  const m = mine.data;
  if (
    !m ||
    m.enforcement === "OFF" ||
    !m.employee ||
    m.employee.discountBudget == null
  ) {
    return null;
  }

  const budget = m.employee.discountBudget;
  const remaining = m.employee.discountRemaining ?? 0;
  const exceeds = quoteDiscount > remaining;
  const after = remaining - quoteDiscount;

  return (
    <div className={`alert ${exceeds ? "alert-danger" : "alert-info"} mb-4`}>
      <Icon name={exceeds ? "alert" : "wallet"} size={18} />
      <div>
        رصيد خصوماتك هذا الشهر: المتبقّي {money(remaining)} من {money(budget)}.
        {quoteDiscount > 0 && <> خصم هذا العرض: {money(quoteDiscount)}.</>}
        {exceeds && m.enforcement === "BLOCK" && (
          <> يتجاوز رصيدك، فلن تتمكّن من إرساله حتى تخفض الخصم أو يرسله مدير.</>
        )}
        {exceeds && m.enforcement === "WARN" && (
          <> يتجاوز رصيدك، والإرسال مسموح مع التنبيه.</>
        )}
        {!exceeds && quoteDiscount > 0 && <> يتبقّى بعده {money(after)}.</>}
      </div>
    </div>
  );
}
